import React, { FC, useEffect, useState } from 'react';
import { T } from 'src/shared/helpers';
import ArrowLeftIcon from 'src/shared/icons/arrow-left-solid.svg';
import CubeIcon from 'src/shared/icons/cube-solid.svg';
import ImageIcon from 'src/shared/icons/images-solid.svg';
import styled, { keyframes } from 'styled-components';
import { Icon } from '../../../../widgets/icon';
import { TOOLS } from '../../constants';
import { fadeIn } from '../../styles/animations';
import { GenerationResult, InputImageData, ProductSideImage, ToolType, VideoResult } from '../../types';
import { ResultItem } from '../results/resultItem';
import { ToolEmptyStateImage } from '../shared/ToolEmptyStateImage';
import { ProductViewer3D } from './viewer3D';

// ============================================
// Loading Animation
// ============================================

const textFade = keyframes`
	0%, 100% {
		opacity: 0;
		transform: translateY(4px);
	}
	10%, 90% {
		opacity: 1;
		transform: translateY(0);
	}
`;

const orbDrift1 = keyframes`
	0%   { transform: translate(-50%, -50%) translate(0px, 0px) scale(1); }
	33%  { transform: translate(-50%, -50%) translate(40px, -30px) scale(1.1); }
	66%  { transform: translate(-50%, -50%) translate(-20px, 50px) scale(0.95); }
	100% { transform: translate(-50%, -50%) translate(0px, 0px) scale(1); }
`;

const orbDrift2 = keyframes`
	0%   { transform: translate(-50%, -50%) translate(0px, 0px) scale(1); }
	33%  { transform: translate(-50%, -50%) translate(-50px, 40px) scale(1.08); }
	66%  { transform: translate(-50%, -50%) translate(30px, -60px) scale(0.92); }
	100% { transform: translate(-50%, -50%) translate(0px, 0px) scale(1); }
`;

/** Compute fake progress (0-95%) based on elapsed time using an ease-out curve */
function computeProgress(startedAt: number | null | undefined, duration: number = 120): number {
    if (!startedAt) return 0;
    const elapsed = (Date.now() - startedAt) / 1000;
    const t = Math.min(elapsed / duration, 1);
    return 95 * (1 - Math.pow(1 - t, 3));
}

const IMAGE_GENERATING_PHRASES = ['Generating...', 'Creating your image...', 'Almost there...'];
const VIDEO_GENERATING_PHRASES = ['Generating...', 'Creating your video...', 'Almost there...'];

const RotatingLoadingText: FC<{ toolId?: ToolType }> = ({ toolId }) => {
    const phrases = toolId === 'video-generation' ? VIDEO_GENERATING_PHRASES : IMAGE_GENERATING_PHRASES;
    const [phraseIndex, setPhraseIndex] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setPhraseIndex((prev) => (prev + 1) % phrases.length);
        }, 3000);
        return () => clearInterval(interval);
    }, [phrases]);

    return <RotatingText key={phraseIndex}>{T._(phrases[phraseIndex], 'Admin')}</RotatingText>;
};

const GeneratingProgress: FC<{ startedAt: number | null | undefined; fakeDuration?: number }> = ({ startedAt, fakeDuration }) => {
    const [progress, setProgress] = useState(() => computeProgress(startedAt, fakeDuration));

    useEffect(() => {
        setProgress(computeProgress(startedAt, fakeDuration));
        const interval = setInterval(() => {
            setProgress(computeProgress(startedAt, fakeDuration));
        }, 1000);
        return () => clearInterval(interval);
    }, [startedAt, fakeDuration]);

    return (
        <ProgressBarContainer>
            <ProgressBar $width={progress} />
        </ProgressBarContainer>
    );
};

// ============================================
// Styled Components
// ============================================

const RotatingText = styled.div`
    font-size: 12px;
    color: #78909c;
    animation: ${textFade} 3s ease-in-out infinite;
`;

const ProgressBarContainer = styled.div`
    width: 80%;
    height: 4px;
    background: rgba(147, 51, 234, 0.15);
    border-radius: 2px;
    overflow: hidden;
`;

const ProgressBar = styled.div<{ $width: number }>`
    height: 100%;
    width: ${(props) => props.$width}%;
    background: linear-gradient(90deg, #7c3aed 0%, #a855f7 50%, #ec4899 100%);
    border-radius: 2px;
    transition: width 1s ease-out;
`;

const Container = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-self: flex-start;
    align-items: center;
    padding: 32px;
    min-height: 0;
    overflow: auto;
    background: transparent;

    @media (max-height: 720px) {
        padding: 20px;
    }

    @media (max-height: 660px) {
        padding: 14px;
    }
`;

const EmptyStateContainer = styled.div`
    position: relative;
    display: flex;
    flex: 1;
    width: 100%;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    background: transparent;
    animation: ${fadeIn} 0.3s ease;
`;

const EmptyOrb1 = styled.div`
    position: absolute;
    top: 15%;
    left: 20%;
    width: 480px;
    height: 480px;
    border-radius: 50%;
    background: rgba(124, 58, 237, 0.07);
    filter: blur(100px);
    pointer-events: none;
    transform: translate(-50%, -50%);
    animation: ${orbDrift1} 12s ease-in-out infinite;
`;

const EmptyOrb2 = styled.div`
    position: absolute;
    top: 80%;
    left: 78%;
    width: 380px;
    height: 380px;
    border-radius: 50%;
    background: rgba(236, 72, 153, 0.06);
    filter: blur(85px);
    pointer-events: none;
    transform: translate(-50%, -50%);
    animation: ${orbDrift2} 16s ease-in-out infinite;
`;

const EmptyInnerContent = styled.div`
    position: relative;
    z-index: 5;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
`;

const EmptyText = styled.div`
    font-size: 14px;
    color: rgba(30, 10, 60, 0.55);
    text-align: center;
    line-height: 1.5;
    max-width: 500px;
`;

const ProductInputContainer = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    animation: ${fadeIn} 0.3s ease;
    align-self: stretch;
    justify-self: stretch;
`;

const ProductInputTitle = styled.div`
    font-size: 16px;
    font-weight: 600;
    color: #374151;
`;

const ViewModeToggle = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    background: #eee;
    border-radius: 9999px;
    padding: 4px;
`;

const ViewModeSlider = styled.div<{ $position: 'left' | 'right' }>`
    position: absolute;
    top: 4px;
    left: ${(props) => (props.$position === 'left' ? '4px' : '50%')};
    width: calc(50% - 4px);
    height: calc(100% - 8px);
    background: #ffffff;
    border-radius: 9999px;
    transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 0;
`;

const ViewModeButton = styled.button<{ $active: boolean }>`
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border: none;
    border-radius: 9999px;
    background: transparent;
    color: #000000;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: color 0.3s ease;
    white-space: nowrap;

    > * {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    svg {
        width: 14px;
        height: 14px;
        fill: currentColor;
    }
`;

const CustomInputCard = styled.div`
    position: relative;
    width: 100%;
    max-width: 400px;
    height: 350px;
    border-radius: 12px;
    overflow: hidden;
    border: 2px solid #3b82f6;
    background: white;
`;

const CustomInputImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: contain;
`;

const ResultsViewContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    width: 100%;
    max-width: min(720px, calc(100vh - 340px));
    margin: 0 auto;
    animation: ${fadeIn} 0.3s ease;

    @media (max-height: 720px) {
        max-width: min(560px, calc(100vh - 300px));
        gap: 16px;
    }

    @media (max-height: 660px) {
        max-width: min(460px, calc(100vh - 280px));
        gap: 12px;
    }
`;

const NavigationButton = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: #3b82f6;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    align-self: center;

    &:hover {
        background: white;
    }

    > * {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    svg {
        width: 14px;
        height: 14px;
        fill: currentColor;
    }
`;

const ShowOutputButton = styled(NavigationButton)`
    align-self: flex-start;
    margin: auto;
    margin-bottom: 10px;

    svg {
        transform: rotate(180deg);
    }
`;

const ResultsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
`;

const EmptyResultCard = styled.div`
    aspect-ratio: 1;
    border-radius: 12px;
    background: white;
    border: 2px dashed #e5e7eb;
`;

const aiGradient = keyframes`
	0% { background-position: 0% 50%; }
	50% { background-position: 100% 50%; }
	100% { background-position: 0% 50%; }
`;

const aiShine = keyframes`
	0% { left: -100%; }
	50%, 100% { left: 100%; }
`;

const SkeletonAI = styled.div`
    background: linear-gradient(
        45deg,
        rgba(147, 51, 234, 0.1) 0%,
        rgba(79, 70, 229, 0.1) 25%,
        rgba(59, 130, 246, 0.1) 50%,
        rgba(16, 185, 129, 0.1) 75%,
        rgba(147, 51, 234, 0.1) 100%
    );
    background-size: 400% 400%;
    animation: ${aiGradient} 3s ease infinite;
    border-radius: 8px;
    position: relative;
    overflow: clip;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.3) 50%, transparent 100%);
        animation: ${aiShine} 2s ease-in-out infinite;
    }
`;

const GeneratingCard = styled(SkeletonAI)`
    aspect-ratio: 1;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    border: 1px solid rgba(147, 51, 234, 0.2);
`;

// ============================================
// Sub-views
// ============================================

interface GalleryViewProps {
    results: (GenerationResult | VideoResult)[];
    isGenerating: boolean;
    generationStartedAt?: number | null;
    sourceToolID?: ToolType;
    onDelete?: (resultId: string) => void;
    onUseAsInput?: (imageUrl: string) => void;
}

const GalleryView: FC<GalleryViewProps> = ({ results, isGenerating, generationStartedAt, sourceToolID, onDelete, onUseAsInput }) => {
    const generatingCount = isGenerating ? 1 : 0;
    const emptyCount = Math.max(0, 4 - results.length - generatingCount);

    return (
        <Container>
            <ResultsViewContainer>
                <ResultsGrid>
                    {isGenerating && (
                        <GeneratingCard>
                            <RotatingLoadingText toolId={sourceToolID} />
                            <GeneratingProgress startedAt={generationStartedAt} fakeDuration={TOOLS.find((t) => t.id === sourceToolID)?.fakeDuration} />
                        </GeneratingCard>
                    )}
                    {results.map((result) => (
                        <ResultItem key={result.id} result={result} onDelete={onDelete} onUseAsInput={onUseAsInput} showTimestamp={false} />
                    ))}
                    {Array.from({ length: emptyCount }).map((_, i) => (
                        <EmptyResultCard key={`empty-${i}`} />
                    ))}
                </ResultsGrid>
            </ResultsViewContainer>
        </Container>
    );
};

interface ProductInputEditorProps {
    productImages?: ProductSideImage[];
    selectedImageIndex: number;
    has3D: boolean;
    productId?: number;
    viewMode: 'images' | '3d';
    onViewModeChange: (mode: 'images' | '3d') => void;
    hasResults: boolean;
    model3DRootUrl: string | null | undefined;
    model3D: string | Blob | null | undefined;
    onGoToGallery?: () => void;
}

const ProductInputEditor: FC<ProductInputEditorProps> = ({
    productImages,
    selectedImageIndex,
    has3D,
    productId,
    viewMode,
    onViewModeChange,
    hasResults,
    model3DRootUrl,
    model3D,
    onGoToGallery
}) => {
    const hasProductImages = productImages && productImages.length > 0;
    const canShow3D = has3D && productId;

    return (
        <Container>
            {hasResults && onGoToGallery && (
                <ShowOutputButton onClick={onGoToGallery}>
                    {T._('Show output', 'Admin')}
                    <Icon>
                        <ArrowLeftIcon />
                    </Icon>
                </ShowOutputButton>
            )}
            <ProductInputContainer>
                <ProductInputTitle>{T._('Input Image', 'Admin')}</ProductInputTitle>

                {hasProductImages && canShow3D && (
                    <ViewModeToggle>
                        <ViewModeSlider $position={viewMode === 'images' ? 'left' : 'right'} />
                        <ViewModeButton $active={viewMode === 'images'} onClick={() => onViewModeChange('images')}>
                            <Icon>
                                <ImageIcon />
                            </Icon>
                            {T._('Images', 'Admin')}
                        </ViewModeButton>
                        <ViewModeButton $active={viewMode === '3d'} onClick={() => onViewModeChange('3d')}>
                            <Icon>
                                <CubeIcon />
                            </Icon>
                            {T._('3D View', 'Admin')}
                        </ViewModeButton>
                    </ViewModeToggle>
                )}

                {viewMode === 'images' && hasProductImages && (
                    <CustomInputCard>
                        <CustomInputImage
                            src={productImages[Math.min(selectedImageIndex, productImages.length - 1)].url}
                            alt={productImages[Math.min(selectedImageIndex, productImages.length - 1)].sideName || T._('Product image', 'Admin')}
                        />
                    </CustomInputCard>
                )}

                {viewMode === '3d' && canShow3D && <ProductViewer3D productId={productId} model3DRootUrl={model3DRootUrl} model3D={model3D} />}

                <EmptyText>
                    {viewMode === '3d'
                        ? T._('Rotate the 3D view to your desired angle, choose options and/or type a prompt to generate your AI scene', 'Admin')
                        : T._('Choose options and/or type a prompt to generate your AI scene', 'Admin')}
                </EmptyText>
            </ProductInputContainer>
        </Container>
    );
};

// ============================================
// Main CanvasArea Component
// ============================================

interface CanvasAreaProps {
    view: 'editor' | 'gallery';
    results: (GenerationResult | VideoResult)[];
    isGenerating: boolean;
    generationStartedAt?: number | null;
    onGoToEditor: () => void;
    onGoToGallery?: () => void;
    onDelete?: (resultId: string) => void;
    onUseAsInput?: (imageUrl: string) => void;
    sourceToolID?: ToolType;
    productImages?: ProductSideImage[];
    selectedImageIndex?: number;
    has3D?: boolean;
    productId?: number;
    model3DRootUrl: string | null | undefined;
    model3D: string | Blob | null | undefined;
    viewMode: 'images' | '3d';
    onViewModeChange: (mode: 'images' | '3d') => void;
    inputImages?: InputImageData[];
    onClearInputImages?: () => void;
    isProductInputEditing?: boolean;
}

export const CanvasArea: FC<CanvasAreaProps> = ({
    view,
    results,
    isGenerating,
    generationStartedAt,
    onGoToGallery,
    onDelete,
    onUseAsInput,
    sourceToolID,
    productImages,
    selectedImageIndex = 0,
    has3D = false,
    productId,
    model3DRootUrl,
    model3D,
    viewMode,
    onViewModeChange,
    isProductInputEditing = false
}) => {
    const hasResults = results.length > 0 || isGenerating;
    
    if (view === 'gallery') {
        return (
            <GalleryView
                results={results}
                isGenerating={isGenerating}
                generationStartedAt={generationStartedAt}
                sourceToolID={sourceToolID}
                onDelete={onDelete}
                onUseAsInput={onUseAsInput}
            />
        );
    }

    const hasProductImages = productImages && productImages.length > 0;
    const canShow3D = has3D && productId;

    if ((hasProductImages || canShow3D) && isProductInputEditing) {
        return (
            <ProductInputEditor
                productImages={productImages}
                selectedImageIndex={selectedImageIndex}
                has3D={has3D}
                productId={productId}
                viewMode={viewMode}
                onViewModeChange={onViewModeChange}
                hasResults={hasResults}
                onGoToGallery={onGoToGallery}
                model3DRootUrl={model3DRootUrl}
                model3D={model3D}
            />
        );
    }

    const toolConfig = TOOLS.find((tool) => tool.id === sourceToolID);
    const emptyText = toolConfig?.description
        ? T._(toolConfig.description, 'Admin')
        : T._('Select your options below and click Generate to see AI-powered results', 'Admin');

    return (
        <Container>
            {hasResults && onGoToGallery && (
                <ShowOutputButton onClick={onGoToGallery}>
                    {T._('Show output', 'Admin')}
                    <Icon>
                        <ArrowLeftIcon />
                    </Icon>
                </ShowOutputButton>
            )}
            <EmptyStateContainer>
                <EmptyOrb1 />
                <EmptyOrb2 />
                <EmptyInnerContent>
                    {sourceToolID && <ToolEmptyStateImage toolId={sourceToolID} />}
                    <EmptyText>{emptyText}</EmptyText>
                </EmptyInnerContent>
            </EmptyStateContainer>
        </Container>
    );
};

export default CanvasArea;
